import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const NextStepsRecommendations = ({ recommendations, onSelectMission, onViewLearningPath }) => {
  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Beginner':
        return 'bg-success/20 text-success';
      case 'Intermediate':
        return 'bg-warning/20 text-warning';
      case 'Advanced':
        return 'bg-error/20 text-error';
      default:
        return 'bg-muted/20 text-muted-foreground';
    }
  };

  const getRecommendationIcon = (type) => {
    switch (type) {
      case 'next_mission':
        return 'ArrowRight';
      case 'skill_building':
        return 'BookOpen';
      case 'challenge':
        return 'Zap';
      case 'review':
        return 'RotateCcw';
      default:
        return 'Target';
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'next_mission':
        return 'Next Mission';
      case 'skill_building':
        return 'Skill Building';
      case 'challenge':
        return 'Challenge';
      case 'review':
        return 'Review';
      default:
        return 'Recommended';
    }
  };

  return (
    <div className="bg-card rounded-lg p-6 border border-subtle">
      <div className="flex items-center gap-3 mb-6">
        <Icon name="Compass" size={24} color="var(--color-primary)" />
        <h2 className="text-xl font-semibold text-foreground">Next Steps</h2>
      </div>
      <div className="space-y-6">
        {/* Primary Recommendation */}
        {recommendations?.primary && (
          <div className="border-2 border-primary/20 rounded-lg p-6 bg-primary/5">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center flex-shrink-0">
                <Icon name="Star" size={20} color="var(--color-primary)" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-lg font-semibold text-foreground">
                    Recommended Next
                  </h3>
                  <span className="px-2 py-1 bg-primary/20 text-primary text-xs font-medium rounded-full">
                    BEST MATCH
                  </span>
                </div>
                <h4 className="font-medium text-foreground mb-2">
                  {recommendations?.primary?.title}
                </h4>
                <p className="text-sm text-muted-foreground mb-4">
                  {recommendations?.primary?.description}
                </p>
                <div className="flex items-center gap-4 mb-4">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${getDifficultyColor(recommendations?.primary?.difficulty)}`}>
                    {recommendations?.primary?.difficulty}
                  </span>
                  <div className="flex items-center gap-1 text-sm text-muted-foreground">
                    <Icon name="Clock" size={14} />
                    <span>{recommendations?.primary?.estimatedTime}</span>
                  </div>
                  <div className="flex items-center gap-1 text-sm text-muted-foreground">
                    <Icon name="Users" size={14} />
                    <span>{recommendations?.primary?.completionRate}% completion rate</span>
                  </div>
                </div>
                <Button
                  variant="default"
                  iconName="Play"
                  iconPosition="left"
                  onClick={() => onSelectMission(recommendations?.primary)}
                >
                  Start Mission
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Other Recommendations */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {recommendations?.others?.map((rec, index) => (
            <div key={index} className="border border-subtle rounded-lg p-4 hover:shadow-elevation-1 transition-shadow">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-muted/50 rounded-full flex items-center justify-center flex-shrink-0">
                  <Icon name={getRecommendationIcon(rec?.type)} size={16} />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                      {getTypeLabel(rec?.type)}
                    </span>
                    {rec?.isUnlocked === false && (
                      <Icon name="Lock" size={12} color="var(--color-muted-foreground)" />
                    )}
                  </div>
                  <h4 className="font-medium text-foreground mb-2">
                    {rec?.title}
                  </h4>
                  <p className="text-sm text-muted-foreground mb-3">
                    {rec?.description}
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${getDifficultyColor(rec?.difficulty)}`}>
                        {rec?.difficulty}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {rec?.estimatedTime}
                      </span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={rec?.isUnlocked === false}
                      onClick={() => onSelectMission(rec)}
                    >
                      {rec?.isUnlocked === false ? 'Locked' : 'Start'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Learning Path Section */}
        {recommendations?.learningPath && (
          <div className="border border-subtle rounded-lg p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Icon name="Route" size={20} color="var(--color-secondary)" />
                <h3 className="font-semibold text-foreground">
                  Continue Learning Path
                </h3>
              </div>
              <Button
                variant="outline"
                size="sm"
                iconName="ExternalLink"
                iconPosition="right"
                onClick={() => onViewLearningPath(recommendations?.learningPath)}
              >
                View Full Path
              </Button>
            </div>
            
            <div className="flex items-center gap-4 mb-3">
              <div className="flex-1">
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-muted-foreground">
                    {recommendations?.learningPath?.name}
                  </span>
                  <span className="text-foreground font-medium">
                    {recommendations?.learningPath?.progress?.completed}/{recommendations?.learningPath?.progress?.total} missions
                  </span>
                </div>
                <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-secondary transition-all duration-500"
                    style={{ 
                      width: `${(recommendations?.learningPath?.progress?.completed / recommendations?.learningPath?.progress?.total) * 100}%` 
                    }}
                  />
                </div>
              </div>
            </div>
            
            <p className="text-sm text-muted-foreground">
              {recommendations?.learningPath?.description}
            </p>
          </div>
        )}

        {/* Skills to Develop */}
        {recommendations?.skillsToImprove && recommendations?.skillsToImprove?.length > 0 && (
          <div className="border border-subtle rounded-lg p-4">
            <div className="flex items-center gap-3 mb-4">
              <Icon name="TrendingUp" size={20} color="var(--color-accent)" />
              <h3 className="font-semibold text-foreground">Skills to Improve</h3>
            </div>
            
            <div className="flex flex-wrap gap-2">
              {recommendations?.skillsToImprove?.map((skill, index) => (
                <div key={index} className="flex items-center gap-2 px-3 py-2 bg-muted/50 rounded-lg">
                  <Icon name="Target" size={14} />
                  <span className="text-sm text-foreground">{skill?.name}</span>
                  <div className="w-12 h-1 bg-muted rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-accent transition-all duration-300"
                      style={{ width: `${skill?.proficiency}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NextStepsRecommendations;